import { useState, useEffect } from "react"

// Offset from the scroll container's top edge (px) at which a section
// is considered "current". Slightly above where scroll-mt-24 (96px)
// lands sections after scrollIntoView, so the just-scrolled-to section
// is always detected.
const SCROLL_OFFSET = 130

/**
 * Tracks which section is currently at the top of the scroll area.
 */
export function useActiveSection(sectionKeys: string[]): string | null {
  const [activeKey, setActiveKey] = useState<string | null>(null)

  useEffect(() => {
    if (sectionKeys.length === 0) return

    const firstEl = document.getElementById(`section-${sectionKeys[0]}`)
    if (!firstEl) return

    const scrollContainer =
      firstEl.closest(".overflow-auto") as HTMLElement | null

    let rafId: number | null = null

    const calculate = () => {
      const containerTop = scrollContainer
        ? scrollContainer.getBoundingClientRect().top
        : 0
      const threshold = containerTop + SCROLL_OFFSET

      let current: string | null = null
      for (const key of sectionKeys) {
        const el = document.getElementById(`section-${key}`)
        if (el && el.getBoundingClientRect().top <= threshold) {
          current = key
        }
      }

      setActiveKey(current ?? sectionKeys[0])
    }

    const onScroll = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        calculate()
      })
    }

    calculate()

    const target = scrollContainer ?? window
    target.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      target.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [sectionKeys.join(",")])

  return activeKey
}
